import type { ResolvedPadding } from "./types";

const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export const toCssString = (value: unknown, fallback = ""): string => {
  if (value == null || typeof value === "object") return fallback;
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  return fallback;
};

export const parseNumericStyle = (value: unknown, fallback = 0): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

export const formatPaddingValue = (padding: ResolvedPadding): string => {
  const top = parseNumericStyle(padding.paddingTop);
  const right = parseNumericStyle(padding.paddingRight);
  const bottom = parseNumericStyle(padding.paddingBottom);
  const left = parseNumericStyle(padding.paddingLeft);

  return `${top}px ${right}px ${bottom}px ${left}px`;
};

export const extractPaddingValues = (
  padding: ResolvedPadding,
): { top: number; right: number; bottom: number; left: number } => ({
  top: parseNumericStyle(padding.paddingTop),
  right: parseNumericStyle(padding.paddingRight),
  bottom: parseNumericStyle(padding.paddingBottom),
  left: parseNumericStyle(padding.paddingLeft),
});

export const extractPlainText = (text: unknown): string => {
  if (typeof text === "string") return text;

  if (!text) return "";

  if (typeof text === "object") {
    const obj = text as Record<string, unknown>;

    if ("text" in obj && typeof obj.text === "string") {
      return obj.text;
    }

    if ("content" in obj && Array.isArray(obj.content)) {
      const extractFromContent = (content: unknown[]): string =>
        content
          .map((node: unknown) => {
            if (!node || typeof node !== "object") return "";
            const nodeObj = node as Record<string, unknown>;

            if (nodeObj.text && typeof nodeObj.text === "string") {
              return nodeObj.text;
            }

            if (nodeObj.content && Array.isArray(nodeObj.content)) {
              return extractFromContent(nodeObj.content);
            }

            return "";
          })
          .join("");

      return extractFromContent(obj.content);
    }
  }

  if (typeof text === "number" || typeof text === "boolean") {
    return String(text);
  }

  return "";
};

export const escapeHtmlForMso = (text: unknown): string => {
  const plainText = extractPlainText(text);
  const withoutHtml = plainText.replace(/<[^<>]*>/g, "");
  return withoutHtml.replace(/[&<>"']/g, char => HTML_ESCAPE_MAP[char] || char);
};
